import {
  attachmentSchema,
  categorySchema,
  commentSchema,
  createCommentResponseSchema,
  createTopicResponseSchema,
  directorySearchResultSchema,
  externalInviteSchema,
  mainCategorySchema,
  memberSchema,
  meResponseSchema,
  moderationQueueItemSchema,
  reportsSummarySchema,
  topicListSchema,
  topicSchema,
  triggerWordRuleSchema,
  voteResultSchema,
  type Attachment,
  type Category,
  type CastVote,
  type Comment,
  type CreateCommentResponse,
  type CreateMainCategory,
  type CreateTopic,
  type CreateTopicResponse,
  type CreateTriggerWordRule,
  type DirectorySearchResult,
  type ExternalInvite,
  type GrantableRole,
  type InviteExternalPartner,
  type InviteMember,
  type ListTopicsQuery,
  type MainCategory,
  type Member,
  type MeResponse,
  type ModerationQueueItem,
  type ReportsSummary,
  type ReportsWindow,
  type Topic,
  type TopicList,
  type TriggerWordRule,
  type UpdateCategory,
  type UpdateComment,
  type UpdateMainCategory,
  type UpdateTopic,
  type UpdateTriggerWordRule,
  type VoteResult,
} from '@mmf/contracts';
import { z } from 'zod';

/**
 * Same-origin fetch with the session cookie attached.
 *
 * There is no Authorization header and no token in memory or storage — the
 * backend-for-frontend holds all of that. `credentials: 'same-origin'` is the
 * whole authentication story on this side.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const hasBody = init.body !== undefined;
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(hasBody && { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  });

  if (!response.ok) {
    // The API returns a structured error, but never assume it did — a proxy or
    // App Service itself can return HTML on a bad day.
    const message = await response.text().catch(() => '');
    throw new ApiError(response.status, message.slice(0, 500) || response.statusText);
  }

  // Every endpoint whose contract type is Promise<void> (approve, reject,
  // delete, report, ...) has nothing to send back and the API leaves the
  // body empty — response.json() throws SyntaxError on empty text, which
  // used to surface as a false "failed" for an action that had actually
  // already succeeded server-side. Read as text first and only parse when
  // there is something there.
  const text = await response.text();
  return text ? JSON.parse(text) : undefined;
}

export async function fetchMe(): Promise<MeResponse> {
  const payload = await request('/api/me');

  // Validating the response against the shared schema means a backend change
  // that breaks the contract fails loudly here rather than rendering undefined
  // somewhere three components away.
  return meResponseSchema.parse(payload);
}

export function signInUrl(returnTo: string = window.location.pathname): string {
  return `/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
}

/** Business partners — a genuinely separate Entra External ID tenant, see ExternalAuthController. */
export function externalSignInUrl(returnTo: string = window.location.pathname): string {
  return `/api/auth/external/login?returnTo=${encodeURIComponent(returnTo)}`;
}

const categoryListSchema = z.array(categorySchema);

/** Active categories for one main category, as any member sees them. */
export async function fetchCategories(mainCategoryId: string): Promise<Category[]> {
  const params = new URLSearchParams({ mainCategoryId });
  return categoryListSchema.parse(await request(`/api/categories?${params.toString()}`));
}

/** Every category for one main category, including deactivated ones. Administrators only. */
export async function fetchAdminCategories(mainCategoryId: string): Promise<Category[]> {
  const params = new URLSearchParams({ mainCategoryId });
  return categoryListSchema.parse(await request(`/api/admin/categories?${params.toString()}`));
}

export async function updateCategory(id: string, patch: UpdateCategory): Promise<Category> {
  const payload = await request(`/api/admin/categories/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return categorySchema.parse(payload);
}

const mainCategoryListSchema = z.array(mainCategorySchema);

/** Active main categories — the /discussions picker. */
export async function fetchMainCategories(): Promise<MainCategory[]> {
  return mainCategoryListSchema.parse(await request('/api/main-categories'));
}

/** Every main category including deactivated ones. Administrators only. */
export async function fetchAdminMainCategories(): Promise<MainCategory[]> {
  return mainCategoryListSchema.parse(await request('/api/admin/main-categories'));
}

export async function createMainCategory(input: CreateMainCategory): Promise<MainCategory> {
  const payload = await request('/api/admin/main-categories', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return mainCategorySchema.parse(payload);
}

export async function updateMainCategory(id: string, patch: UpdateMainCategory): Promise<MainCategory> {
  const payload = await request(`/api/admin/main-categories/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return mainCategorySchema.parse(payload);
}

export async function fetchTopics(query: Partial<ListTopicsQuery> = {}): Promise<TopicList> {
  const params = new URLSearchParams();
  if (query.mainCategoryId) {
    params.set('mainCategoryId', query.mainCategoryId);
  }
  if (query.category) {
    params.set('category', query.category);
  }
  if (query.sort) {
    params.set('sort', query.sort);
  }
  if (query.page) {
    params.set('page', String(query.page));
  }
  if (query.mine) {
    params.set('mine', 'true');
  }
  if (query.q) {
    params.set('q', query.q);
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return topicListSchema.parse(await request(`/api/topics${suffix}`));
}

export async function fetchTopic(id: string): Promise<Topic> {
  return topicSchema.parse(await request(`/api/topics/${encodeURIComponent(id)}`));
}

export async function createTopic(input: CreateTopic): Promise<CreateTopicResponse> {
  const payload = await request('/api/topics', { method: 'POST', body: JSON.stringify(input) });
  return createTopicResponseSchema.parse(payload);
}

/** Author self-edit. */
export async function updateTopic(id: string, patch: UpdateTopic): Promise<Topic> {
  const payload = await request(`/api/topics/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return topicSchema.parse(payload);
}

/** Author self-delete — see TopicRepository.remove for what this actually does. */
export async function deleteTopic(id: string): Promise<void> {
  await request(`/api/topics/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

const commentListSchema = z.array(commentSchema);

export async function fetchComments(topicId: string): Promise<Comment[]> {
  return commentListSchema.parse(
    await request(`/api/topics/${encodeURIComponent(topicId)}/comments`),
  );
}

/** Full body, unlike the moderation queue's truncated excerpt — what the moderator inline-edit form loads. */
export async function fetchComment(id: string): Promise<Comment> {
  return commentSchema.parse(await request(`/api/comments/${encodeURIComponent(id)}`));
}

export async function createComment(
  topicId: string,
  input: { body: string; parentCommentId?: string },
): Promise<CreateCommentResponse> {
  const payload = await request(`/api/topics/${encodeURIComponent(topicId)}/comments`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return createCommentResponseSchema.parse(payload);
}

/** Author self-edit. */
export async function updateComment(id: string, patch: UpdateComment): Promise<Comment> {
  const payload = await request(`/api/comments/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return commentSchema.parse(payload);
}

/** Author self-delete — see CommentRepository.remove for what this actually does. */
export async function deleteComment(id: string): Promise<void> {
  await request(`/api/comments/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// --- Attachments -------------------------------------------------------

const attachmentListSchema = z.array(attachmentSchema);

export async function fetchAttachments(kind: 'topic' | 'comment', id: string): Promise<Attachment[]> {
  const resource = kind === 'topic' ? 'topics' : 'comments';
  return attachmentListSchema.parse(await request(`/api/${resource}/${encodeURIComponent(id)}/attachments`));
}

/**
 * The proxy download endpoint, not a raw blob URL — this is what makes the
 * visibility rule (Published/own/moderator, plus never anything short of
 * scanState Clean) actually apply. Safe to use directly as an <img src> or
 * <a href>; the browser's own GET carries the session cookie.
 */
export function attachmentDownloadUrl(id: string): string {
  return `/api/attachments/${encodeURIComponent(id)}/download`;
}

/**
 * The one caller in this file that isn't JSON — a multipart body needs the
 * browser to set its own `Content-Type: multipart/form-data; boundary=...`,
 * which `request()`'s hardcoded `application/json` header would break.
 * `piiAcknowledged` must be appended to the FormData before `file` — the API
 * reads multipart fields in send order and validates the checkbox before it
 * starts streaming the file into blob storage.
 */
export async function uploadAttachment(
  kind: 'topic' | 'comment',
  id: string,
  file: File,
  piiAcknowledged: boolean,
): Promise<Attachment> {
  const resource = kind === 'topic' ? 'topics' : 'comments';
  const formData = new FormData();
  formData.append('piiAcknowledged', piiAcknowledged ? 'true' : 'false');
  formData.append('file', file);

  const response = await fetch(`/api/${resource}/${encodeURIComponent(id)}/attachments`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    body: formData,
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new ApiError(response.status, message.slice(0, 500) || response.statusText);
  }

  const text = await response.text();
  return attachmentSchema.parse(text ? JSON.parse(text) : undefined);
}

/** Author self-delete. */
export async function deleteAttachment(id: string): Promise<void> {
  await request(`/api/attachments/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function castVote(
  kind: 'topic' | 'comment',
  id: string,
  value: CastVote['value'],
): Promise<VoteResult> {
  const resource = kind === 'topic' ? 'topics' : 'comments';
  const payload = await request(`/api/${resource}/${encodeURIComponent(id)}/votes`, {
    method: 'POST',
    body: JSON.stringify({ value }),
  });
  return voteResultSchema.parse(payload);
}

/** Flags a topic/comment for a moderator to review. No ownership check on the API side. */
export async function reportItem(kind: 'topic' | 'comment', id: string, reason: string): Promise<void> {
  const resource = kind === 'topic' ? 'topics' : 'comments';
  await request(`/api/${resource}/${encodeURIComponent(id)}/report`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// --- Member administration -------------------------------------------------

const directorySearchResultListSchema = z.array(directorySearchResultSchema);

export async function searchDirectory(query: string): Promise<DirectorySearchResult[]> {
  const params = new URLSearchParams({ q: query });
  return directorySearchResultListSchema.parse(
    await request(`/api/admin/directory/search?${params.toString()}`),
  );
}

const memberListSchema = z.array(memberSchema);

export async function fetchMembers(): Promise<Member[]> {
  return memberListSchema.parse(await request('/api/admin/members'));
}

export async function inviteMember(input: InviteMember): Promise<Member> {
  const payload = await request('/api/admin/members/invite', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return memberSchema.parse(payload);
}

export async function suspendMember(id: string, reason: string): Promise<Member> {
  const payload = await request(`/api/admin/members/${encodeURIComponent(id)}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return memberSchema.parse(payload);
}

export async function restoreMember(id: string): Promise<Member> {
  const payload = await request(`/api/admin/members/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  });
  return memberSchema.parse(payload);
}

export async function removeMember(id: string): Promise<Member> {
  const payload = await request(`/api/admin/members/${encodeURIComponent(id)}/remove`, {
    method: 'POST',
  });
  return memberSchema.parse(payload);
}

export async function grantRole(id: string, role: GrantableRole): Promise<Member> {
  const payload = await request(
    `/api/admin/members/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}`,
    { method: 'POST' },
  );
  return memberSchema.parse(payload);
}

export async function revokeRole(id: string, role: GrantableRole): Promise<Member> {
  const payload = await request(
    `/api/admin/members/${encodeURIComponent(id)}/roles/${encodeURIComponent(role)}`,
    { method: 'DELETE' },
  );
  return memberSchema.parse(payload);
}

export async function inviteExternalPartner(input: InviteExternalPartner): Promise<ExternalInvite> {
  const payload = await request('/api/admin/members/invite-external', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return externalInviteSchema.parse(payload);
}

const externalInviteListSchema = z.array(externalInviteSchema);

export async function fetchExternalInvites(): Promise<ExternalInvite[]> {
  return externalInviteListSchema.parse(await request('/api/admin/members/external-invites'));
}

// --- Moderation --------------------------------------------------------

const moderationQueueSchema = z.array(moderationQueueItemSchema);

export async function fetchModerationQueue(): Promise<ModerationQueueItem[]> {
  return moderationQueueSchema.parse(await request('/api/admin/moderation/queue'));
}

export async function approveModerationItem(
  targetType: 'Topic' | 'Comment',
  targetId: string,
): Promise<void> {
  await request(
    `/api/admin/moderation/${targetType}/${encodeURIComponent(targetId)}/approve`,
    { method: 'POST' },
  );
}

export async function rejectModerationItem(
  targetType: 'Topic' | 'Comment',
  targetId: string,
): Promise<void> {
  await request(
    `/api/admin/moderation/${targetType}/${encodeURIComponent(targetId)}/reject`,
    { method: 'POST' },
  );
}

/**
 * A moderator correcting a held submission before deciding — e.g. redacting
 * PII a Block rule caught. `title` is required for a Topic, omitted for a
 * Comment — matches the API's own per-targetType schema choice.
 */
export async function editModerationItem(
  targetType: 'Topic' | 'Comment',
  targetId: string,
  patch: { title?: string; body: string },
): Promise<void> {
  await request(`/api/admin/moderation/${targetType}/${encodeURIComponent(targetId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

const triggerWordRuleListSchema = z.array(triggerWordRuleSchema);

export async function fetchTriggerWordRules(): Promise<TriggerWordRule[]> {
  return triggerWordRuleListSchema.parse(await request('/api/admin/trigger-rules'));
}

export async function createTriggerWordRule(
  input: CreateTriggerWordRule,
): Promise<TriggerWordRule> {
  const payload = await request('/api/admin/trigger-rules', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return triggerWordRuleSchema.parse(payload);
}

export async function updateTriggerWordRule(
  id: string,
  patch: UpdateTriggerWordRule,
): Promise<TriggerWordRule> {
  const payload = await request(`/api/admin/trigger-rules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return triggerWordRuleSchema.parse(payload);
}

// --- Reporting -----------------------------------------------------------

export async function fetchReportsSummary(window: ReportsWindow): Promise<ReportsSummary> {
  const params = new URLSearchParams({ window });
  return reportsSummarySchema.parse(await request(`/api/admin/reports?${params.toString()}`));
}
